import React, { useState, useEffect, useCallback } from "react";
import AsyncSelect from "react-select/async";
import { customFetch } from '../apiClient';
import * as XLSX from "xlsx"; 

import defaultLogo from "../assests/kamlaLogo.png"; 

const selectStyles = {
  control: (base, state) => ({
    ...base, minHeight: '34px', fontSize: '12px', borderRadius: '6px',
    borderColor: state.isFocused ? 'var(--theme-color, #0088cc)' : '#cbd5e0',
    boxShadow: state.isFocused ? '0 0 0 3px color-mix(in srgb, var(--theme-color, #0088cc), transparent 85%)' : 'none',
    '&:hover': { borderColor: 'var(--theme-color, #0088cc)' }, borderWidth: '1px'
  }),
  menu: (base) => ({ ...base, zIndex: 9999, borderRadius: '6px', fontSize: '12px' }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base, padding: '8px 12px',
    backgroundColor: state.isSelected ? 'var(--theme-color, #0088cc)' : state.isFocused ? 'color-mix(in srgb, var(--theme-color, #0088cc), white 90%)' : 'white',
    color: state.isSelected ? 'white' : '#333', cursor: 'pointer',
    '&:active': { backgroundColor: 'var(--theme-color, #0088cc)', color: 'white' }
  }),
  placeholder: (base) => ({ ...base, color: '#94a3b8', fontSize: '12px' })
};

const InventoryReports = () => {
  const [activeTab, setActiveTab] = useState("current-stock");
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [companyInfo, setCompanyInfo] = useState(null);
  
  // Ledger Specific State
  const [selectedMedicine, setSelectedMedicine] = useState(null);

  // ✅ Advanced Features States
  const [globalSearch, setGlobalSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [summary, setSummary] = useState({ totalItems: 0, totalValue: 0, totalStock: 0, totalIn: 0, totalOut: 0 });

  useEffect(() => {
    const fetchCompanyInfo = async () => {
     try {
        const res = await customFetch(`/PharmaCompany`);
        if (res.ok) {
          const data = await res.json();
          if (data && data.length > 0) setCompanyInfo(data[0]);
        }
      } catch (err) { console.error("Failed to fetch company info", err); }
    };
    fetchCompanyInfo();
  }, []);

  const fetchReport = async () => {
    setLoading(true); setError(""); setReportData([]); setCurrentPage(1);
 try {
      let url = "";

      if (activeTab === "current-stock") url = `/Reports/inventory/current-stock`;
      else if (activeTab === "reorder") url = `/Reports/inventory/reorder`;
      else if (activeTab === "valuation") url = `/Reports/inventory/valuation`;
      else if (activeTab === "ledger") {
         if(!selectedMedicine) { setLoading(false); return; } 
         url = `/Reports/inventory/ledger/${selectedMedicine.value}`;
      }

      const res = await customFetch(url);
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();

      let actualData = activeTab === "valuation" ? data.items || [] : data || [];
      setReportData(actualData);

      // ✅ Dynamic Summary Calculation
      let sTotalValue = 0, sTotalStock = 0, sTotalIn = 0, sTotalOut = 0;
      
      if (activeTab === "valuation") {
         sTotalValue = data.grandTotal || 0;
      } else if (activeTab === "current-stock") {
         sTotalStock = actualData.reduce((s, i) => s + (i.balancePieces || i.BalancePieces || 0), 0);
         sTotalValue = actualData.reduce((s, i) => s + ((i.balancePieces || i.BalancePieces || 0) * (i.purchaseRate || i.PurchaseRate || 0)), 0);
      } else if (activeTab === "ledger") {
         sTotalIn = actualData.reduce((s, i) => s + ((i.qtyInPieces || i.QtyInPieces || 0) > 0 ? (i.qtyInPieces || i.QtyInPieces) : 0), 0);
         sTotalOut = actualData.reduce((s, i) => s + ((i.qtyInPieces || i.QtyInPieces || 0) < 0 ? Math.abs(i.qtyInPieces || i.QtyInPieces) : 0), 0);
      }

      setSummary({ totalItems: actualData.length, totalValue: sTotalValue, totalStock: sTotalStock, totalIn: sTotalIn, totalOut: sTotalOut });

    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  useEffect(() => {
    setGlobalSearch(""); // Reset search on tab change
    if (activeTab !== "ledger") fetchReport();
    else setReportData([]); 
  }, [activeTab]);

  const loadMedicineOptions = async (inputValue) => {
    if (!inputValue || !inputValue.trim()) return [];
try {
      const res = await customFetch(`/MedicineDT/search?medicine=${encodeURIComponent(inputValue.trim())}`);
      if (!res.ok) return [];
      const data = await res.json();
      return data.map((m) => ({ value: m.medID, label: m.medicine }));
    } catch (err) { return []; }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const d = new Date(dateString);
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;
  };

  // ✅ Client-side Filtering & Pagination
  const filteredData = reportData.filter(item => {
      if (!globalSearch) return true;
      const searchLower = globalSearch.toLowerCase();
      const medName = (item.medicine || item.Medicine || '').toLowerCase();
      const batch = (item.batchNo || item.BatchNo || '').toLowerCase();
      return medName.includes(searchLower) || batch.includes(searchLower);
  });

  const totalPages = Math.ceil(filteredData.length / rowsPerPage) || 1;
  const displayedData = filteredData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  // ==============================================================
  // ✅ EXCEL EXPORT
  // ==============================================================
  const exportToExcel = useCallback(() => {
    if (filteredData.length === 0) { alert("No data available to export!"); return; }

    let formattedData = [];
    if (activeTab === 'current-stock') {
      formattedData = filteredData.map((item, i) => ({
        "Sr": i + 1, "Medicine": item.medicine || item.Medicine, "Brand": item.brandName || item.BrandName, 
        "Batch": item.batchNo || item.BatchNo, "Exp Date": formatDate(item.expDate || item.ExpDate),
        "Pack": item.packSize || item.PackSize, "Balance (Box)": item.balanceQty || item.BalanceQty,
        "Balance (Pcs)": item.balancePieces || item.BalancePieces, "MRP": item.mrp || item.MRP, "Pur. Rate": item.purchaseRate || item.PurchaseRate
      }));
    } else if (activeTab === 'reorder') {
      formattedData = filteredData.map((item, i) => ({
        "Sr": i + 1, "Medicine": item.medicine || item.Medicine, "Brand": item.brandName || item.BrandName, 
        "Pack Size": item.packSize || item.PackSize, "Current Stock (Box)": item.currentStockBox || item.CurrentStockBox,
        "Current Stock (Pcs)": item.currentStockPieces || item.CurrentStockPieces, "Status": "Low Stock"
      }));
    } else if (activeTab === 'valuation') {
      formattedData = filteredData.map((item, i) => ({
        "Sr": i + 1, "Medicine": item.medicine || item.Medicine, "Pack Size": item.packSize || item.PackSize,
        "Balance (Pcs)": item.balancePieces || item.BalancePieces, "Purchase Rate": item.purchaseRate || item.PurchaseRate,
        "Total Value": item.totalValue || item.TotalValue
      }));
    } else if (activeTab === 'ledger') {
      formattedData = filteredData.map((item, i) => ({
        "Date": formatDate(item.date || item.Date), "Type": item.type || item.Type, 
        "Bill No": item.billNo || item.BillNo, "Party Name": item.partyName || item.PartyName,
        "In/Out (Pcs)": item.qtyInPieces || item.QtyInPieces, "Rate": item.rate || item.Rate
      }));
    }

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Inventory_Report");
    XLSX.writeFile(workbook, `INVENTORY_${activeTab.toUpperCase()}.xlsx`);
  }, [filteredData, activeTab]);

  // ==============================================================
  // ✅ TALLY / MARG STYLE PRINT & PDF
  // ==============================================================
  const handlePrint = useCallback(() => {
    if (filteredData.length === 0) { alert("No data available to print!"); return; }

    const logoSource = companyInfo?.logoBase64 ? `data:image/png;base64,${companyInfo.logoBase64}` : defaultLogo;
    const compName = companyInfo?.company_Name || "HOSPITAL / PHARMACY STORE";
    const compAddress = companyInfo?.address || "";
    const compContact = companyInfo?.contact1 ? `Ph: ${companyInfo.contact1}` : "";
    const compGst = companyInfo?.reg_No ? `DL/GST: ${companyInfo.reg_No}` : "";

    const ROWS_PER_PAGE = 40; 
    const pages = [];
    let runningTotal = 0;

    for (let i = 0; i < filteredData.length; i += ROWS_PER_PAGE) {
      const pageItems = filteredData.slice(i, i + ROWS_PER_PAGE);
      const broughtForward = runningTotal;
      
      const pageTotal = pageItems.reduce((sum, item) => {
          if (activeTab === 'valuation') return sum + (item.totalValue || item.TotalValue || 0);
          if (activeTab === 'ledger') return sum + (item.qtyInPieces || item.QtyInPieces || 0);
          return sum + (item.balancePieces || item.BalancePieces || 0);
      }, 0);

      runningTotal += pageTotal;
      
      pages.push({
        items: pageItems,
        startIndex: i,
        broughtForward: broughtForward,
        carriedForward: runningTotal,
        isLastPage: i + ROWS_PER_PAGE >= filteredData.length
      });
    }

    const reportHtml = pages.map((page, pageIndex) => {
      
      let thHtml = "";
      if (activeTab === 'current-stock') thHtml = `<th style="text-align:left">Medicine</th><th style="text-align:left">Batch</th><th style="text-align:center">Exp Date</th><th style="text-align:right">Bal (Box)</th><th style="text-align:right">Bal (Pcs)</th><th style="text-align:right">Pur. Rate</th>`;
      else if (activeTab === 'reorder') thHtml = `<th style="text-align:left">Medicine</th><th style="text-align:left">Brand</th><th style="text-align:center">Pack</th><th style="text-align:right">Stock (Box)</th><th style="text-align:right">Stock (Pcs)</th>`;
      else if (activeTab === 'valuation') thHtml = `<th style="text-align:left">Medicine</th><th style="text-align:center">Pack</th><th style="text-align:right">Bal (Pcs)</th><th style="text-align:right">Pur. Rate</th><th style="text-align:right">Total Value</th>`;
      else if (activeTab === 'ledger') thHtml = `<th style="text-align:left">Date</th><th style="text-align:left">Type</th><th style="text-align:left">Bill No</th><th style="text-align:left">Party Name</th><th style="text-align:right">In/Out (Pcs)</th><th style="text-align:right">Rate</th>`;

      const trHtml = page.items.map((item, idx) => {
        if (activeTab === 'current-stock') return `<tr><td style="text-align:left"><div><small style="color:#555">${item.brandName || item.BrandName || '-'}</small></div><strong>${item.medicine || item.Medicine}</strong></td><td style="text-align:left">${item.batchNo || item.BatchNo || '-'}</td><td style="text-align:center">${formatDate(item.expDate || item.ExpDate)}</td><td style="text-align:right">${item.balanceQty || item.BalanceQty}</td><td style="text-align:right; font-weight:bold;">${item.balancePieces || item.BalancePieces}</td><td style="text-align:right">${item.purchaseRate || item.PurchaseRate}</td></tr>`;
        if (activeTab === 'reorder') return `<tr><td style="text-align:left"><strong>${item.medicine || item.Medicine}</strong></td><td style="text-align:left">${item.brandName || item.BrandName || '-'}</td><td style="text-align:center">${item.packSize || item.PackSize}</td><td style="text-align:right; color:#dc2626;">${item.currentStockBox || item.CurrentStockBox}</td><td style="text-align:right; font-weight:bold;">${item.currentStockPieces || item.CurrentStockPieces}</td></tr>`;
        if (activeTab === 'valuation') return `<tr><td style="text-align:left"><strong>${item.medicine || item.Medicine}</strong></td><td style="text-align:center">${item.packSize || item.PackSize}</td><td style="text-align:right">${item.balancePieces || item.BalancePieces}</td><td style="text-align:right">${item.purchaseRate || item.PurchaseRate}</td><td style="text-align:right; font-weight:bold;">${(item.totalValue || item.TotalValue || 0)?.toFixed(2)}</td></tr>`;
        if (activeTab === 'ledger') {
           const qty = item.qtyInPieces || item.QtyInPieces || 0;
           return `<tr><td style="text-align:left">${formatDate(item.date || item.Date)}</td><td style="text-align:left">${item.type || item.Type}</td><td style="text-align:left">${item.billNo || item.BillNo}</td><td style="text-align:left">${item.partyName || item.PartyName || '-'}</td><td style="text-align:right; font-weight:bold; color:${qty > 0 ? '#16a34a' : '#dc2626'};">${qty > 0 ? '+'+qty : qty}</td><td style="text-align:right">${item.rate || item.Rate}</td></tr>`;
        }
      }).join('');

      let colSpanBF = '4';
      if(activeTab === 'current-stock') colSpanBF = '4';
      if(activeTab === 'reorder') colSpanBF = '4';
      if(activeTab === 'valuation') colSpanBF = '4';
      if(activeTab === 'ledger') colSpanBF = '4';

      const totalLabel = activeTab === 'valuation' ? "Total Value (₹):" : "Total Qty (Pcs):";

      const bfHtml = pageIndex > 0 ? `
        <tr style="background:#f9f9f9; border-bottom: 1px solid #000;">
          <td colspan="${colSpanBF}" style="text-align:right; font-weight:bold; font-style:italic;">Brought Forward (B/F):</td>
          <td style="text-align:right; font-weight:bold;">${page.broughtForward.toFixed(2)}</td>
          ${activeTab === 'current-stock' || activeTab === 'ledger' ? '<td></td>' : ''}
        </tr>` : "";

      const cfHtml = `
        <tr style="background:#e0e0e0;">
          <td colspan="${colSpanBF}" style="text-align:right; font-weight:bold; font-size:12px; padding: 6px;">
             ${page.isLastPage ? `GRAND TOTAL (${totalLabel}):` : 'Carried Forward (C/F):'}
          </td>
          <td style="text-align:right; font-weight:bold; font-size:12px; padding: 6px;">${page.carriedForward.toFixed(2)}</td>
          ${activeTab === 'current-stock' || activeTab === 'ledger' ? '<td></td>' : ''}
        </tr>`;

      return `
        <div class="a4-page ${page.isLastPage ? 'last-page' : ''}">
          <div class="marg-header">
            <img src="${logoSource}" alt="Logo" class="print-logo"/>
            <div class="marg-header-center">
              <h2>${compName}</h2>
              <p>${compAddress}</p>
              <p><strong>${compContact}</strong></p>
            </div>
            <div class="marg-header-right">
              ${compGst ? `<span><strong>${compGst}</strong></span><br/>` : ""}
              <span><strong>Page:</strong> ${pageIndex + 1} of ${pages.length}</span><br/>
              <span><strong>Print Date:</strong> ${new Date().toLocaleDateString('en-GB')}</span>
            </div>
          </div>
          
          <div class="marg-title-strip">
            INVENTORY - ${activeTab.toUpperCase().replace('-', ' ')} REPORT
            ${activeTab === 'ledger' && selectedMedicine ? `<br/>(Item: ${selectedMedicine.label})` : ''}
          </div>

          <div class="content-area">
            <table class="items-table">
              <thead><tr>${thHtml}</tr></thead>
              <tbody>${bfHtml}${trHtml}</tbody>
            </table>
          </div>
          
          <div class="footer-area">
             <table class="items-table" style="margin:0; border-top: 2px solid #000;"><tbody>${cfHtml}</tbody></table>
          </div>
        </div>
      `;
    }).join("");

    const pw = window.open("", "_blank", "width=900,height=700");
    pw.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${activeTab.toUpperCase()} Report</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; font-family: Arial, sans-serif; }
          body { background: #525659; display: flex; flex-direction: column; align-items: center; padding: 20px; }
          @media print {
            @page { size: A4 portrait; margin: 8mm; }
            body { background: #fff; padding: 0; margin: 0;}
            .a4-page { width: 100% !important; min-height: 275mm; page-break-after: always; margin: 0 !important; border: 1px solid #000 !important; }
            .a4-page.last-page { page-break-after: auto; }
          }
          .a4-page { background: #fff; width: 210mm; min-height: 280mm; border: 2px solid #000; margin-bottom: 20mm; display: flex; flex-direction: column; box-shadow: 0 0 10px rgba(0,0,0,0.5); }
          .marg-header { border-bottom: 2px solid #000; padding: 10px 15px; display: flex; justify-content: space-between; align-items: center; }
          .print-logo { width: 75px; height: 75px; object-fit: contain; flex-shrink:0;}
          .marg-header-center { flex-grow: 1; padding: 0 15px; text-align:center;}
          .marg-header-center h2 { margin: 0 0 4px 0; font-size: 18px; text-transform: uppercase; }
          .marg-title-strip { background-color: #e0e0e0 !important; border-bottom: 2px solid #000; text-align: center; padding: 5px; font-weight: bold; font-size: 13px; -webkit-print-color-adjust: exact !important;}
          .content-area { flex-grow: 1; padding: 0; } 
          .footer-area { margin-top: auto; }
          .items-table { width: 100%; border-collapse: collapse; font-size: 11px; }
          .items-table th { border-bottom: 2px solid #000; border-right: 1px solid #ccc; background-color: #f1f1f1 !important; padding: 6px 8px; font-weight: bold; text-transform: uppercase; -webkit-print-color-adjust: exact !important;}
          .items-table td { border-right: 1px dashed #ccc; border-bottom: 1px dashed #ccc; padding: 5px 8px; }
          .items-table th:last-child, .items-table td:last-child { border-right: none; }
        </style>
      </head>
      <body>${reportHtml}</body>
      </html>
    `);
    pw.document.close();
    setTimeout(() => { pw.focus(); pw.print(); }, 500);
  }, [filteredData, activeTab, companyInfo, selectedMedicine]);

  useEffect(() => {
    const handleExportEvent = (e) => {
      const exportType = e.detail?.type;
      if (exportType === 'excel') exportToExcel();
      else if (exportType === 'pdf' || exportType === 'print') handlePrint(); 
    };
    window.addEventListener("exportData", handleExportEvent);
    return () => window.removeEventListener("exportData", handleExportEvent);
  }, [exportToExcel, handlePrint]);

  return (
    <div className="report-container">
      <div className="report-card">
        
        {/* Header */}
        <div className="card-header">
          <div className="header-left">
             <div className="header-icon"><i className="bi bi-box-seam"></i></div>
             <div className="header-text"><h5>Inventory Reports</h5><p>Stock, Valuation & History</p></div>
          </div>
          <button className="btn-header-action" onClick={fetchReport} disabled={loading || (activeTab === 'ledger' && !selectedMedicine)}>
             <i className={`bi bi-arrow-clockwise ${loading ? 'spin' : ''}`}></i> Refresh
          </button>
        </div>

        {/* Tabs Navigation */}
        <div className="tabs-header">
           <button className={`tab-btn ${activeTab === 'current-stock' ? 'active' : ''}`} onClick={() => setActiveTab('current-stock')}>
              <i className="bi bi-inboxes"></i> Current Stock
           </button>
           <button className={`tab-btn ${activeTab === 'reorder' ? 'active' : ''}`} onClick={() => setActiveTab('reorder')}>
              <i className="bi bi-exclamation-triangle"></i> Re-Order Level
           </button>
           <button className={`tab-btn ${activeTab === 'valuation' ? 'active' : ''}`} onClick={() => setActiveTab('valuation')}>
              <i className="bi bi-currency-rupee"></i> Valuation
           </button>
           <button className={`tab-btn ${activeTab === 'ledger' ? 'active' : ''}`} onClick={() => setActiveTab('ledger')}>
              <i className="bi bi-journal-text"></i> Item Ledger
           </button>
        </div>

        <div className="card-content">
          {error && <div className="alert-custom alert-danger">{error}</div>}

          {/* Controls Bar */}
          <div className="filter-bar">
             {activeTab === 'ledger' ? (
                <div className="form-group flex-grow-1" style={{maxWidth: '400px'}}>
                   <label>Select Medicine for Ledger:</label>
                   <AsyncSelect 
                       cacheOptions defaultOptions loadOptions={loadMedicineOptions} 
                       onChange={(opt) => { setSelectedMedicine(opt); }} 
                       value={selectedMedicine} placeholder="Search Medicine..." 
                       styles={selectStyles} menuPortalTarget={document.body} 
                   />
                </div>
             ) : (
                <div className="form-group flex-grow-1" style={{maxWidth: '300px'}}>
                   <label>Quick Search:</label>
                   <input type="text" className="form-input" placeholder="Search by Medicine or Batch..." 
                          value={globalSearch} onChange={(e) => { setGlobalSearch(e.target.value); setCurrentPage(1); }} />
                </div>
             )}

             {activeTab === 'ledger' && (
                 <button className="btn-search" onClick={fetchReport} disabled={loading || !selectedMedicine}>
                    {loading ? 'Wait...' : 'Get Ledger'}
                 </button>
             )}
          </div>

          {/* ✅ SUMMARY CARDS */}
          {!loading && reportData.length > 0 && (
             <div className="summary-cards">
                <div className="s-card total">
                   <div className="s-label">{activeTab === 'ledger' ? 'Total Transactions' : 'Total Items Listed'}</div>
                   <div className="s-value">{summary.totalItems}</div>
                </div>
                
                {(activeTab === 'current-stock' || activeTab === 'valuation') && (
                   <div className="s-card cash">
                      <div className="s-label">Est. Inventory Value</div>
                      <div className="s-value">₹ {summary.totalValue.toLocaleString('en-IN', {minimumFractionDigits: 2})}</div>
                   </div>
                )}
                
                {activeTab === 'current-stock' && (
                   <div className="s-card upi">
                      <div className="s-label">Total Pieces in Stock</div>
                      <div className="s-value">{summary.totalStock.toLocaleString('en-IN')} Pcs</div>
                   </div>
                )}

                {activeTab === 'ledger' && (
                   <>
                    <div className="s-card cash">
                       <div className="s-label">Total Inward (Pcs)</div>
                       <div className="s-value">+{summary.totalIn}</div>
                    </div>
                    <div className="s-card returns">
                       <div className="s-label">Total Outward (Pcs)</div>
                       <div className="s-value">-{summary.totalOut}</div>
                    </div>
                   </>
                )}
             </div>
          )}

          {/* Table Area */}
          <div className="table-section">
            <div className="table-responsive">
              <table className="custom-table">
                <thead>
                  {activeTab === 'current-stock' && (
                     <tr>
                        <th className="text-center" style={{width:'40px'}}>#</th>
                        <th className="text-start">Medicine Name</th><th className="text-start">Brand</th><th className="text-start">Batch</th><th className="text-center">Exp Date</th><th className="text-center">Pack</th>
                        <th className="text-end">Balance (Box)</th>
                        <th className="text-end">Balance (Pcs)</th>
                        <th className="text-end">MRP</th><th className="text-end">Pur. Rate</th>
                     </tr>
                  )}
                  {activeTab === 'reorder' && (
                     <tr>
                        <th className="text-center" style={{width:'40px'}}>#</th>
                        <th className="text-start">Medicine Name</th><th className="text-start">Brand</th><th className="text-center">Pack Size</th>
                        <th className="text-end text-danger">Current Stock (Box)</th>
                        <th className="text-end">Current Stock (Pcs)</th>
                        <th className="text-center">Status</th>
                     </tr>
                  )}
                  {activeTab === 'valuation' && (
                     <tr>
                        <th className="text-center" style={{width:'40px'}}>#</th>
                        <th className="text-start">Medicine Name</th><th className="text-center">Pack Size</th>
                        <th className="text-end">Balance (Pcs)</th>
                        <th className="text-end">Purchase Rate</th>
                        <th className="text-end">Total Value</th>
                     </tr>
                  )}
                  {activeTab === 'ledger' && (
                     <tr>
                        <th className="text-start">Date</th><th className="text-start">Type</th><th className="text-start">Bill No</th><th className="text-start">Party Name</th>
                        <th className="text-end">In/Out (Pcs)</th>
                        <th className="text-end">Rate</th>
                     </tr>
                  )}
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="10" className="empty-state">Loading data...</td></tr>
                  ) : displayedData.length === 0 ? (
                    <tr><td colSpan="10" className="empty-state">No records found.</td></tr>
                  ) : (
                    displayedData.map((item, index) => {
                      const qty = item.qtyInPieces || item.QtyInPieces || 0;
                      return (
                      <tr key={index}>
                        {activeTab !== 'ledger' && <td className="text-center text-muted text-sm">{(currentPage - 1) * rowsPerPage + index + 1}</td>}
                        
                        {activeTab === 'current-stock' && <>
                           <td className="text-start fw-bold">{item.medicine || item.Medicine}</td>
                           <td className="text-start text-muted text-sm">{item.brandName || item.BrandName || '-'}</td>
                           <td className="text-start">{item.batchNo || item.BatchNo || '-'}</td>
                           <td className="text-center" style={{color: new Date(item.expDate || item.ExpDate) < new Date() ? '#dc2626' : 'inherit', fontWeight: new Date(item.expDate || item.ExpDate) < new Date() ? 'bold' : 'normal'}}>
                              {formatDate(item.expDate || item.ExpDate)}
                           </td>
                           <td className="text-center">{item.packSize || item.PackSize}</td>
                           <td className="text-end">{item.balanceQty || item.BalanceQty} <small className="text-muted">{item.mUnit || item.MUnit}</small></td>
                           <td className="text-end fw-bold text-primary">{item.balancePieces || item.BalancePieces}</td>
                           <td className="text-end">₹{item.mrp || item.MRP}</td>
                           <td className="text-end text-muted">₹{item.purchaseRate || item.PurchaseRate}</td>
                        </>}

                        {activeTab === 'reorder' && <>
                           <td className="text-start fw-bold">{item.medicine || item.Medicine}</td>
                           <td className="text-start text-muted text-sm">{item.brandName || item.BrandName || '-'}</td>
                           <td className="text-center">{item.packSize || item.PackSize}</td>
                           <td className="text-end fw-bold text-danger">{item.currentStockBox || item.CurrentStockBox}</td>
                           <td className="text-end fw-medium">{item.currentStockPieces || item.CurrentStockPieces}</td>
                           <td className="text-center"><span className="badge-low">Low Stock</span></td>
                        </>}

                        {activeTab === 'valuation' && <>
                           <td className="text-start fw-bold">{item.medicine || item.Medicine}</td>
                           <td className="text-center">{item.packSize || item.PackSize}</td>
                           <td className="text-end fw-medium">{item.balancePieces || item.BalancePieces}</td>
                           <td className="text-end text-muted">₹{item.purchaseRate || item.PurchaseRate}</td>
                           <td className="text-end fw-bold text-success">₹{(item.totalValue || item.TotalValue || 0)?.toFixed(2)}</td>
                        </>}

                        {activeTab === 'ledger' && <>
                           <td className="text-start text-muted">{formatDate(item.date || item.Date)}</td>
                           <td className="text-start">
                              <span className={`badge-type ${(item.type || item.Type) === 'PURCHASE' || (item.type || item.Type) === 'OPENING' ? 'bg-in' : 'bg-out'}`}>
                                 {item.type || item.Type}
                              </span>
                           </td>
                           <td className="text-start fw-medium">{item.billNo || item.BillNo}</td>
                           <td className="text-start">{item.partyName || item.PartyName || '-'}</td>
                           <td className={`text-end fw-bold ${qty > 0 ? 'text-success' : 'text-danger'}`}>
                              {qty > 0 ? `+${qty}` : qty}
                           </td>
                           <td className="text-end">₹{item.rate || item.Rate}</td>
                        </>}
                      </tr>
                    )})
                  )}
                </tbody>
              </table>
            </div>

            {/* ✅ Pagination Controls */}
            {!loading && filteredData.length > 0 && (
              <div className="d-flex justify-content-between align-items-center p-2 border-top bg-light">
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted" style={{fontSize:'11px'}}>Rows per page:</span>
                  <select 
                    className="form-select form-select-sm" 
                    style={{width: '70px', fontSize: '11px', padding: '2px 10px'}}
                    value={rowsPerPage} 
                    onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                  >
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
                
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted" style={{fontSize:'11px', marginRight:'10px'}}>
                    Showing {(currentPage - 1) * rowsPerPage + 1} to {Math.min(currentPage * rowsPerPage, filteredData.length)} of {filteredData.length} entries
                  </span>
                  <button 
                    className="btn btn-sm btn-outline-secondary py-1 px-2" 
                    disabled={currentPage === 1} 
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  ><i className="bi bi-chevron-left"></i> Prev</button>
                  <span className="fw-bold" style={{fontSize:'11px'}}> Page {currentPage} of {totalPages} </span>
                  <button 
                    className="btn btn-sm btn-outline-secondary py-1 px-2" 
                    disabled={currentPage === totalPages} 
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  >Next <i className="bi bi-chevron-right"></i></button>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* ✅ CSS STYLES */}
      <style jsx>{`
        .report-container { padding: 0; font-size: 12px; }
        .report-card { background: white; border-radius: 8px; box-shadow: 0 1px 10px rgba(0,0,0,0.05); border: 1px solid #e0e6ed; overflow: hidden; }
        
        .card-header { background: var(--theme-color, #0088cc); padding: 8px 15px; display: flex; justify-content: space-between; align-items: center; }
        .header-left { display: flex; align-items: center; gap: 10px; color: white; }
        .header-icon { width: 32px; height: 32px; background: rgba(255, 255, 255, 0.2); border-radius: 6px; display: flex; align-items: center; justify-content: center; font-size: 16px; }
        .header-text h5 { margin: 0; font-size: 15px; font-weight: 700; }
        .header-text p { margin: 0; color: rgba(255, 255, 255, 0.85); font-size: 11px; }

        .btn-header-action { padding: 5px 12px; border-radius: 4px; border: 1px solid rgba(255, 255, 255, 0.4); background: rgba(255, 255, 255, 0.15); color: white; font-size: 11px; font-weight: 600; cursor: pointer; transition: 0.2s; display: flex; align-items: center; gap: 6px; }
        .btn-header-action:hover { background: rgba(255, 255, 255, 0.25); }
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { 100% { transform: rotate(360deg); } }

        .tabs-header { display: flex; background: #f8fafc; border-bottom: 1px solid #e0e6ed; padding: 0 15px; gap: 5px; overflow-x: auto; }
        .tab-btn { padding: 10px 14px; border: none; background: transparent; color: #64748b; font-weight: 600; font-size: 12px; cursor: pointer; border-bottom: 2px solid transparent; display: flex; gap: 6px; align-items: center; white-space: nowrap; }
        .tab-btn:hover { color: var(--theme-color, #0088cc); }
        .tab-btn.active { color: var(--theme-color, #0088cc); border-bottom-color: var(--theme-color, #0088cc); background: white; border-radius: 6px 6px 0 0; border: 1px solid #e0e6ed; border-bottom: none; position: relative; top: 1px; }

        .card-content { padding: 15px; }

        .filter-bar { display: flex; gap: 8px; background: #f8fafc; padding: 10px; border-radius: 6px; border: 1px solid #e0e6ed; margin-bottom: 12px; align-items: flex-end; }
        .form-group { display: flex; flex-direction: column; }
        .form-group label { font-size: 11px; font-weight: 600; margin-bottom: 3px; color: #475569; }
        .form-input { padding: 4px 8px; border: 1px solid #cbd5e1; border-radius: 4px; font-size: 12px; height: 34px; outline: none; }
        .btn-search { padding: 0 15px; background: var(--theme-color, #0088cc); color: white; border: none; border-radius: 4px; font-weight: 600; cursor: pointer; height: 34px; font-size: 12px; }
        .btn-search:disabled { opacity: 0.6; cursor: not-allowed; }

        /* Summary Cards */
        .summary-cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 10px; margin-bottom: 15px; }
        .s-card { padding: 12px; border-radius: 8px; color: white; position: relative; overflow: hidden; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
        .s-card.total { background: linear-gradient(135deg, #3b82f6, #2563eb); }
        .s-card.cash { background: linear-gradient(135deg, #10b981, #059669); }
        .s-card.upi { background: linear-gradient(135deg, #0ea5e9, #0284c7); }
        .s-card.returns { background: linear-gradient(135deg, #ef4444, #b91c1c); }
        .s-label { font-size: 11px; opacity: 0.9; margin-bottom: 3px; text-transform: uppercase; }
        .s-value { font-size: 18px; font-weight: 700; }

        .table-section { background: white; border-radius: 6px; border: 1px solid #e0e6ed; overflow: hidden; }
        .table-responsive { overflow-x: auto; }
        .custom-table { width: 100%; border-collapse: collapse; font-size: 12px; }
        .custom-table th { background: #f8fafc; color: #334155; padding: 8px 10px; text-align: left; font-weight: 600; border-bottom: 2px solid #e2e8f0; white-space: nowrap; }
        .custom-table td { padding: 6px 10px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; color: #1e293b; }
        .custom-table tr:hover { background-color: #f8fafc; }

        .text-start { text-align: left !important; }
        .text-center { text-align: center !important; }
        .text-end { text-align: right !important; }
        .fw-bold { font-weight: 700; }
        .fw-medium { font-weight: 500; }
        .text-success { color: #16a34a !important; }
        .text-danger { color: #dc2626 !important; }
        .text-primary { color: var(--theme-color, #0088cc) !important; }
        .text-muted { color: #64748b !important; }
        .text-sm { font-size: 10px; }

        .badge-low { background: #fee2e2; color: #b91c1c; padding: 3px 8px; border-radius: 4px; font-size: 10px; font-weight: 600; border: 1px solid #fecaca; }
        .badge-type { padding: 3px 8px; border-radius: 4px; font-size: 10px; font-weight: 600; text-transform: uppercase; }
        .bg-in { background: #dcfce7; color: #15803d; }
        .bg-out { background: #fee2e2; color: #b91c1c; }

        .empty-state { text-align: center; padding: 40px; color: #94a3b8; font-style: italic; }
        .alert-custom { padding: 10px 14px; border-radius: 8px; font-size: 12px; margin-bottom: 14px; }
        .alert-danger { background: #fee2e2; color: #b91c1c; border: 1px solid #fecaca; }
      `}</style>
    </div>
  );
};

export default InventoryReports;